import {
  resolveSalonSetup,
  STORED_SALON_MODULES,
  type SalonSetup,
} from '@repo/shared/salon-modules';
import { useGetOrganizationSettings } from '@/api/generated/organization/organization';
import type { GetOrganizationSettings200Data } from '@/api/generated/schemas';
import { getOrganizationSettingsOrgQueryKey } from '@/api/organization-query-keys';
import { useOrg } from '@/providers/org-provider';

export interface SalonSetupState {
  /**
   * What Hayven runs next to the salon's POS, as named facts (`resolveSalonSetup`): Calendar, Turns, Check-in and what
   * follows from them (who proves a visit, which money Hayven can show, the turn sheet, …). Every screen reads these
   * facts, never the raw switches: `setup.checkIn` shows or hides check-in itself, `setup.modules.sales` is Hayven as
   * the POS, `setup.doneUsesPerks` says whether a visit Done spends perks.
   */
  setup: SalonSetup;
  /**
   * An agency-assigned Hayven Tap stand answers taps on one of the salon's active locations. With the counter mode
   * (`setup.perkRedeem` COUNTER_QR) it decides who meets Marketing › Hayven Tap.
   */
  hasCounterStand: boolean;
  /** The salon's real setup is known (settings loaded): not just the everything-off fallback. */
  isResolved: boolean;
  /** Settings failed to load: `setup` is only the everything-off fallback. */
  isError: boolean;
}

type SetupSettings = Pick<GetOrganizationSettings200Data, 'setup'> &
  Partial<Pick<GetOrganizationSettings200Data, 'hasCounterStand'>>;

/** Until settings load every add-on reads as off, like the modules, so an off page never flashes in a menu. */
const SETUP_WHILE_UNRESOLVED: SalonSetup = resolveSalonSetup({
  disabledModules: STORED_SALON_MODULES,
  turnTrackerEnabled: false,
  pointOfSaleMode: 'ALONGSIDE_POS',
  checkInMode: 'POS',
  checkInEnabled: false,
});

/** `useSalonSetup` for a settings response read outside React (page prefetches read the shell's cached settings). */
export function getSalonSetupState(settings: SetupSettings | undefined, isError = false): SalonSetupState {
  const setup = settings?.setup ?? SETUP_WHILE_UNRESOLVED;
  return { setup, hasCounterStand: settings?.hasCounterStand ?? false, isResolved: Boolean(settings), isError };
}

/**
 * The salon's setup facts. Reads the SAME settings query (and key) the org shell already prefetches, so it never adds
 * a request; a setup change on another screen refreshes it over realtime (`salon:setup-changed`).
 */
export function useSalonSetup(): SalonSetupState {
  const { shortCode } = useOrg();
  const { data, isError } = useGetOrganizationSettings({
    query: { queryKey: getOrganizationSettingsOrgQueryKey(shortCode) },
  });
  return getSalonSetupState(data?.data, isError);
}
